import React, { useState, useEffect, useMemo } from "react";
import {
  TableContainer,
  Paper,
  Typography,
  CircularProgress,
  Box,
  TextField,
  IconButton,
  Button,
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Drawer,
  Slider,
  FormControlLabel,
  Switch,
  Divider,
} from "@mui/material";
import { Search, ShoppingBag, ArrowUp, ArrowDown, GripVertical, X, Link as LinkIcon, Info, Settings, Banknote, Package } from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import api from '../utils/api';
import ProductTools from '../components/ProductTools';
import Produit from '../components/Produit';

function getColorForValue(value, thresholds) {
  if (value === null || value === undefined) return { bg: 'inherit', text: 'inherit' };
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return { bg: 'inherit', text: 'inherit' };
  if (numValue >= thresholds.high) return { bg: '#e8f5e9', text: '#2e7d32' };
  if (numValue >= thresholds.medium) return { bg: '#fff3e0', text: '#ef6c00' };
  return { bg: '#ffebee', text: '#c62828' };
}

const CommandesPage = () => {
  const [commandes, setCommandes] = useState([]);
  const [produits, setProduits] = useState([]);
  const [achats, setAchats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState([{ id: "DateCommande", desc: true }]);
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('commandesColumnOrder');
    return savedOrder ? JSON.parse(savedOrder) : [];
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const savedSizing = localStorage.getItem('commandesColumnSizing');
    if (savedSizing) {
      return JSON.parse(savedSizing);
    }
    return {
      DateCommande: 120,
      AmazonOrderId: 180,
      Produit: 250,
      Achat: 100,
      Pays: 80,
      PrixDeVente: 120,
      fraisFBA: 100,
      fraisReferral: 100,
      Profit: 120,
      ROI: 100,
      Marge: 100,
      TVA: 100,
      Quantite: 80,
    };
  });
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [filters, setFilters] = useState({
    Statut: { value: '' },
  });
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openAchatDialog, setOpenAchatDialog] = useState(false);
  const [selectedAchat, setSelectedAchat] = useState(null);
  const [viewMode, setViewMode] = useState('card-table');
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [fournisseurs, setFournisseurs] = useState([]);
  const [thresholds, setThresholds] = useState(() => {
    const saved = localStorage.getItem("commandesThresholds");
    return saved ? JSON.parse(saved) : {
      roi: { high: 50, medium: 30, enabled: true },
      profit: { high: 100, medium: 50, enabled: true },
      marge: { high: 30, medium: 15, enabled: true },
    };
  });
  const [isThresholdsOpen, setIsThresholdsOpen] = useState(false);
  const [openRepricerModal, setOpenRepricerModal] = useState(false);

  useEffect(() => {
    localStorage.setItem('commandesThresholds', JSON.stringify(thresholds));
  }, [thresholds]);

  const updateThreshold = (key, field, value) => {
    setThresholds(prev => {
      const newThresholds = {
        ...prev,
        [key]: {
          ...prev[key],
          [field]: value
        }
      };
      localStorage.setItem("commandesThresholds", JSON.stringify(newThresholds));
      return newThresholds;
    });
  };

  // Liste des statuts possibles Amazon
  const ORDER_STATUSES = [
    'Shipped', 'Pending', 'Canceled', 'Refund', 'Business'
  ];
  const STATUS_COLORS = {
    Shipped: 'success',
    Pending: 'warning',
    Canceled: 'error',
    Refund: 'error',
    Business: 'info',
  };

  const [statusFilter, setStatusFilter] = useState(null);
  const [displayCount, setDisplayCount] = useState(10);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [cmdRes, prodRes, achatRes] = await Promise.all([
          api.get("/api/commandes"),
          api.get("/api/produits"),
          api.get("/api/achats"),
        ]);
        setCommandes(Array.isArray(cmdRes.data) ? cmdRes.data : []);
        setProduits(Array.isArray(prodRes.data) ? prodRes.data : []);
        setAchats(Array.isArray(achatRes.data) ? achatRes.data : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem('commandesColumnOrder', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);
  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      localStorage.setItem('commandesColumnSizing', JSON.stringify(columnSizing));
    }
  }, [columnSizing]);

  useEffect(() => {
    api.get('/api/fournisseurs').then(res => setFournisseurs(res.data));
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    let d;
    if (dateStr.endsWith('Z') || dateStr.includes('+')) {
      d = new Date(dateStr);
    } else {
      d = new Date(dateStr + 'Z');
    }
    return d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    });
  };
  const formatAmount = (amount) =>
    amount !== undefined && amount !== null && !isNaN(amount)
      ? Number(amount).toFixed(2) + " €"
      : "-";
  const formatPercentage = (value) =>
    value !== undefined && value !== null && !isNaN(value)
      ? Number(value).toFixed(2) + "%"
      : "-";
  const getProduit = (id) => produits.find((p) => p.id === id);
  const getAchat = (id) => achats.find((a) => a.id === id);

  const handleOpenProductDetail = (productId) => {
    const product = produits.find((p) => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setOpenProductDialog(true);
    }
  };
  const handleCloseProductDialog = () => {
    setOpenProductDialog(false);
    setSelectedProduct(null);
  };
  const handleOpenAchatDetail = (achatId) => {
    const achat = achats.find((a) => a.id === achatId);
    if (achat) {
      setSelectedAchat(achat);
      setOpenAchatDialog(true);
    }
  };
  const handleCloseAchatDialog = () => {
    setOpenAchatDialog(false);
    setSelectedAchat(null);
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.display({
      id: "ImageProduit",
      header: "Image",
      size: 70,
      cell: (info) => {
        const produit = getProduit(info.row.original.Produit);
        const asin = produit?.ASIN;
        const titre = produit?.Titre || "Produit";
        const imgUrl = asin
          ? `https://images-na.ssl-images-amazon.com/images/P/${asin}.01._SCLZZZZZZZ_.jpg`
          : null;
        return imgUrl ? (
          <Tooltip title={titre}>
            <img
              src={imgUrl}
              alt={titre}
              style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 6, background: "#fafafa", border: "1px solid #eee" }}
              onError={e => { e.target.onerror = null; e.target.src = "/no-image.png"; }}
            />
          </Tooltip>
        ) : (
          <Box sx={{ width: 48, height: 48, bgcolor: '#f0f0f0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 22 }}>
            ?
          </Box>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
      enableResizing: false,
    }),
    columnHelper.accessor("DateCommande", {
      header: "DateCommande",
      cell: (info) => formatDate(info.getValue()),
      sortingFn: "datetime",
      enableColumnFilter: true,
      size: columnSizing.DateCommande,
      enableResizing: true,
      filterFn: 'custom',
    }),
    columnHelper.accessor("AmazonOrderId", {
      header: "ID Commande",
      cell: (info) => info.getValue(),
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.AmazonOrderId,
      enableResizing: true,
      filterFn: 'custom',
    }),
    columnHelper.accessor("Produit", {
      header: "Produit",
      cell: (info) => {
        const produit = getProduit(info.getValue());
        return produit ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{produit.Titre}</span>
            <IconButton size="small" onClick={() => handleOpenProductDetail(produit.id)} title="Voir détail du produit">
              <Package size={18} />
            </IconButton>
          </Box>
        ) : "-";
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.Produit,
      enableResizing: true,
      filterFn: 'equals',
    }),
    columnHelper.accessor("Achat", {
      header: "Achat associé",
      cell: (info) => {
        const achat = getAchat(info.getValue());
        return achat ? (
          <IconButton size="small" onClick={() => handleOpenAchatDetail(achat.id)} title="Voir détail de l'achat">
            <LinkIcon size={16} />
          </IconButton>
        ) : "-";
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.Achat,
      enableResizing: true,
      filterFn: 'equals',
    }),
    columnHelper.accessor("Pays", {
      header: "Pays",
      cell: (info) => info.getValue(),
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.Pays,
      enableResizing: true,
      filterFn: 'equals',
    }),
    columnHelper.accessor("PrixDeVente", {
      header: "Prix de vente",
      cell: (info) => formatAmount(info.getValue()),
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.PrixDeVente,
      enableResizing: true,
      filterFn: 'custom',
    }),
    columnHelper.accessor("fraisFBA", {
      header: "Frais FBA",
      cell: (info) => formatAmount(info.getValue()),
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.fraisFBA,
      enableResizing: true,
      filterFn: 'custom',
    }),
    columnHelper.accessor("fraisReferral", {
      header: "Frais Referral",
      cell: (info) => formatAmount(info.getValue()),
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.fraisReferral,
      enableResizing: true,
      filterFn: 'custom',
    }),
    columnHelper.accessor("Profit", {
      header: "Profit",
      cell: (info) => {
        const value = info.getValue();
        const color = value > 0 ? "success" : value < 0 ? "error" : "default";
        return (
          <Chip label={formatAmount(value)} color={color} size="small" sx={{ fontWeight: 600 }} />
        );
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.Profit,
      enableResizing: true,
      filterFn: 'custom',
    }),
    columnHelper.accessor("ROI", {
      header: "ROI",
      cell: (info) => {
        const value = info.getValue();
        let color = "default";
        if (value >= 100) color = "success";
        else if (value >= 50) color = "info";
        else if (value > 0) color = "warning";
        else color = "error";
        return (
          <Chip label={formatPercentage(value)} color={color} size="small" sx={{ fontWeight: 600 }} />
        );
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.ROI,
      enableResizing: true,
      filterFn: 'custom',
    }),
    columnHelper.accessor("Marge", {
      header: "Marge",
      cell: (info) => {
        const value = info.getValue();
        let color = "default";
        if (value >= 50) color = "success";
        else if (value >= 20) color = "info";
        else if (value > 0) color = "warning";
        else color = "error";
        return (
          <Chip label={formatPercentage(value)} color={color} size="small" sx={{ fontWeight: 600 }} />
        );
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.Marge,
      enableResizing: true,
      filterFn: 'custom',
    }),
    columnHelper.accessor("TVA", {
      header: "TVA",
      cell: (info) => formatAmount(info.getValue()),
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.TVA,
      enableResizing: true,
      filterFn: 'custom',
    }),
    columnHelper.accessor("Quantite", {
      header: "Quantité",
      cell: (info) => info.getValue() ?? "-",
      enableColumnFilter: true,
      enableSorting: true,
      size: columnSizing.Quantite,
      enableResizing: true,
      filterFn: 'custom',
    }),
    columnHelper.accessor("cout_achat_total", {
      header: "Coût d'achat total",
      cell: (info) => {
        const value = Number(info.getValue()) || 0;
        return `${value.toFixed(2)} €`;
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: 140,
      enableResizing: true,
    }),
    columnHelper.accessor("Statut", {
      header: "Statut",
      cell: (info) => info.getValue() || "-",
      enableColumnFilter: true,
      enableSorting: true,
      size: 120,
      enableResizing: true,
      filterFn: 'statut',
    }),
    columnHelper.accessor("RefundLossHT", {
      header: "Perte remboursement",
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined) return "-";
        return formatAmount(value);
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: 150,
      enableResizing: true,
    }),
    columnHelper.accessor("RefundDate", {
      header: "Date remboursement",
      cell: (info) => formatDate(info.getValue()),
      enableColumnFilter: true,
      enableSorting: true,
      size: 150,
      enableResizing: true,
    }),
    columnHelper.accessor("RefundReason", {
      header: "Raison remboursement",
      cell: (info) => info.getValue() || "-",
      enableColumnFilter: true,
      enableSorting: true,
      size: 200,
      enableResizing: true,
    }),
    columnHelper.accessor("RefundCategory", {
      header: "Catégorie remboursement",
      cell: (info) => info.getValue() || "-",
      enableColumnFilter: true,
      enableSorting: true,
      size: 150,
      enableResizing: true,
    }),
    columnHelper.accessor("QteRefund", {
      header: "Quantité remboursée",
      cell: (info) => info.getValue() || "-",
      enableColumnFilter: true,
      enableSorting: true,
      size: 150,
      enableResizing: true,
    }),
    columnHelper.accessor("EtatRefund", {
      header: "État remboursement",
      cell: (info) => info.getValue() || "-",
      enableColumnFilter: true,
      enableSorting: true,
      size: 150,
      enableResizing: true,
    }),
    columnHelper.accessor("StatutRefund", {
      header: "Statut remboursement",
      cell: (info) => info.getValue() || "-",
      enableColumnFilter: true,
      enableSorting: true,
      size: 150,
      enableResizing: true,
    }),
    columnHelper.accessor("RefundAmazon", {
      header: "Remboursement Amazon",
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined) return "-";
        return formatAmount(value);
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: 150,
      enableResizing: true,
    }),
  ], [produits, achats, columnSizing]);

  const table = useReactTable({
    data: commandes,
    columns,
    state: {
      globalFilter,
      pagination,
      sorting,
      columnOrder,
      columnSizing,
      columnFilters: Object.entries(filters).map(([id, filter]) => ({ id, value: filter })),
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: (newSizing) => {
      setColumnSizing(newSizing);
      localStorage.setItem('commandesColumnSizing', JSON.stringify(newSizing));
    },
    onColumnFiltersChange: (updater) => {
      if (typeof updater === 'function') {
        const newFilters = updater(Object.entries(filters).map(([id, filter]) => ({ id, value: filter })));
        const updatedFilters = {};
        newFilters.forEach(filter => { updatedFilters[filter.id] = filter.value; });
        setFilters(updatedFilters);
      } else {
        const updatedFilters = {};
        updater.forEach(filter => { updatedFilters[filter.id] = filter.value; });
        setFilters(updatedFilters);
      }
    },
    globalFilterFn: "includesString",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnResizing: true,
    enableColumnOrdering: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
      size: 150,
    },
    filterFns: {
      custom: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        const value = parseFloat(row.getValue(columnId));
        if (isNaN(value)) return true;
        const filterValueNum = parseFloat(filterValue.value);
        if (isNaN(filterValueNum)) return true;
        switch (filterValue.operator) {
          case '>=': return value >= filterValueNum;
          case '>': return value > filterValueNum;
          case '<=': return value <= filterValueNum;
          case '<': return value < filterValueNum;
          case '=': return value === filterValueNum;
          default: return true;
        }
      },
      equals: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        const value = row.getValue(columnId);
        if (typeof value === 'string') {
          return value.toLowerCase().includes(filterValue.value.toLowerCase());
        }
        return value === filterValue.value;
      },
      statut: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        const value = row.getValue(columnId);
        return value === filterValue.value;
      },
    },
    autoResetPageIndex: false,
  });

  const pageCount = table.getPageCount() || 0;

  const calculateTotals = () => {
    return commandes.reduce(
      (acc, commande) => ({
        totalVentes: acc.totalVentes + (Number(commande.PrixDeVente) || 0),
        totalFBA: acc.totalFBA + (Number(commande.fraisFBA) || 0),
        totalReferral: acc.totalReferral + (Number(commande.fraisReferral) || 0),
        totalProfit: acc.totalProfit + (Number(commande.Profit) || 0),
        totalTVA: acc.totalTVA + (Number(commande.TVA) || 0),
      }),
      {
        totalVentes: 0,
        totalFBA: 0,
        totalReferral: 0,
        totalProfit: 0,
        totalTVA: 0,
      }
    );
  };
  const totals = calculateTotals();

  const sortedCommandes = useMemo(() => {
    let data = [...commandes];
    // Filtrage par badge
    if (statusFilter === 'Business') {
      data = data.filter(cmd => cmd.Business === true);
    } else if (statusFilter) {
      data = data.filter(cmd => cmd.Statut === statusFilter);
    }
    // Tri par date (Refund = RefundDate, sinon DateCommande)
    data = data.sort((a, b) => {
      const da = a.Statut === 'Refund' && a.RefundDate ? new Date(a.RefundDate) : new Date(a.DateCommande);
      const db = b.Statut === 'Refund' && b.RefundDate ? new Date(b.RefundDate) : new Date(b.DateCommande);
      return db - da;
    });
    return data;
  }, [commandes, statusFilter]);

  const filteredCommandes = useMemo(() => {
    let data = sortedCommandes;
    if (orderIdFilter) {
      data = data.filter(cmd => 
        cmd.AmazonOrderId.toLowerCase().includes(orderIdFilter.toLowerCase())
      );
    }
    return data;
  }, [sortedCommandes, orderIdFilter]);

  const paginatedCommandes = useMemo(() => filteredCommandes.slice(0, displayCount), [filteredCommandes, displayCount]);

  // Ajoute ce mapping pour les drapeaux pays
  const countryFlags = {
    FR: "https://flagcdn.com/24x18/fr.png",
    ES: "https://flagcdn.com/24x18/es.png",
    IT: "https://flagcdn.com/24x18/it.png",
    DE: "https://flagcdn.com/24x18/de.png",
    UK: "https://flagcdn.com/24x18/gb.png",
    NL: "https://flagcdn.com/24x18/nl.png",
    PL: "https://flagcdn.com/24x18/pl.png",
    SE: "https://flagcdn.com/24x18/se.png",
    BE: "https://flagcdn.com/24x18/be.png",
    IE: "https://flagcdn.com/24x18/ie.png"
  };

  // Dans le mode tableau, retire le badge de statut, affiche juste le texte (dans la colonne Statut)
  // ...
  // Dans le mode moderne, modifie le rendu de la card :
  // - AmazonOrderId : lien Seller Central (selon pays)
  // - Pays : drapeau
  // - Prix de vente et prix d'achat mis en avant
  // - Tooltip sur prix d'achat pour détail
  // ...
  // Dans le mapping des cards (mode moderne) :
  const sellerCentralUrls = {
    FR: 'https://sellercentral.amazon.fr/orders-v3/order/',
    DE: 'https://sellercentral.amazon.de/orders-v3/order/',
    IT: 'https://sellercentral.amazon.it/orders-v3/order/',
    ES: 'https://sellercentral.amazon.es/orders-v3/order/',
    UK: 'https://sellercentral.amazon.co.uk/orders-v3/order/',
    NL: 'https://sellercentral.amazon.nl/orders-v3/order/',
    PL: 'https://sellercentral.amazon.pl/orders-v3/order/',
    SE: 'https://sellercentral.amazon.se/orders-v3/order/',
    BE: 'https://sellercentral.amazon.com.be/orders-v3/order/',
    IE: 'https://sellercentral.amazon.ie/orders-v3/order/'
  };
  // ...
  // Dans le rendu de la card :
  // - AmazonOrderId :
  // ...
  // - Pays :
  // ... existing code ...

  const renderCard = (row) => {
    const produit = getProduit(row.Produit);
    const achat = getAchat(row.Achat);
    const asin = produit?.ASIN;
    const imgUrl = asin ? `https://images-na.ssl-images-amazon.com/images/P/${asin}.01._SCLZZZZZZZ_.jpg` : null;
    const titre = produit?.Titre || "Produit inconnu";
    const statut = row.Statut || '-';
    const orderLink = sellerCentralUrls[row.Pays] ? sellerCentralUrls[row.Pays] + row.AmazonOrderId : null;
    const flagUrl = countryFlags[row.Pays];
    const flagUrlLivraison = countryFlags[row.PaysLivraison];
    const prixAchat = achat?.prixAchat || achat?.["Prix d'achat"] || 0;
    const txTvaAchat = achat?.TxTVA || null;
    const txTvaVente = row.TxTVA || null;
    const tvaCollectee = (row.PrixDeVente && txTvaVente) ? ((Number(row.PrixDeVente) / (1 + Number(txTvaVente)/100)) * (Number(txTvaVente)/100)) : null;
    const tvaDeductible = (row.prixAchatAchat && row.txTvaAchat)
      ? ((Number(row.prixAchatAchat) / (1 + Number(row.txTvaAchat)/100)) * (Number(row.txTvaAchat)/100))
      : null;
    const fraisPrep = achat?.frais_preparation || 0;
    const fraisLiv = achat?.frais_livraison || 0;
    const totalFees = (Number(row.fraisFBA) || 0) + (Number(row.fraisReferral) || 0) + (Number(row.feeDigitalServices) || 0) + (Number(row.feeVariable) || 0);
    const profitColors = getColorForValue(row.Profit, thresholds.profit);
    const roiColors = getColorForValue(row.ROI, thresholds.roi);
    const margeColors = getColorForValue(row.Marge, thresholds.marge);
    
    // Créer les listes de frais non nuls
    const amazonFees = [];
    if (Number(row.fraisFBA) > 0) amazonFees.push(`FBA : ${formatAmount(row.fraisFBA)}`);
    if (Number(row.fraisReferral) > 0) amazonFees.push(`Referral : ${formatAmount(row.fraisReferral)}`);
    if (Number(row.feeDigitalServices) > 0) amazonFees.push(`Services numériques : ${formatAmount(row.feeDigitalServices)}`);
    if (Number(row.feeVariable) > 0) amazonFees.push(`Variable closing : ${formatAmount(row.feeVariable)}`);

    const fraisEtTvaTooltip = (
      <Box>
        {amazonFees.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#000', fontWeight: 700 }}>Frais Amazon</Typography>
            <Box sx={{ mb: 2 }}>
              {amazonFees.map((fee, index) => (
                <div key={index}>{fee}</div>
              ))}
            </Box>
          </>
        )}
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#000', fontWeight: 700 }}>TVA</Typography>
        <Box>
          <div>TVA collectée : {tvaCollectee !== null ? formatAmount(tvaCollectee) : '-'} {txTvaVente ? `- TVA : ${txTvaVente}%` : ''}</div>
          <div>TVA déductible : {tvaDeductible !== null ? formatAmount(tvaDeductible * (row.Quantite || 1)) : '-'} {row.txTvaAchat ? `- TVA : ${row.txTvaAchat}%` : ''}</div>
        </Box>
      </Box>
    );

    // Tooltip pour le coût total
    const coutTotalTooltip = (
      <Box>
        <div>Prix d'achat : {prixAchat > 0 ? formatAmount(prixAchat) : '-'}</div>
        {fraisPrep > 0 && <div>Frais de préparation : {formatAmount(fraisPrep)}</div>}
        {fraisLiv > 0 && <div>Frais de livraison : {formatAmount(fraisLiv)}</div>}
      </Box>
    );

    return (
      <Card
        key={row.id}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          p: 2,
          mb: 3.5,
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          bgcolor: '#fff',
          transition: 'all 0.2s ease',
          '&:hover': { 
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transform: 'translateY(-1px)'
          },
          maxWidth: 1200,
          margin: '0 auto',
          position: 'relative',
          border: row.Statut === 'Refund' ? '1.5px solid #ff9800' : '1px solid #eee',
        }}
      >
        {/* Colonne de gauche : Image et infos de base */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          flex: { md: '0 0 300px' },
          borderRight: { md: '1px solid #eee' },
          pr: { md: 2 },
          mr: { md: 2 }
        }}>
          {/* Image du produit */}
          <Box sx={{ 
            width: 64, 
            height: 64, 
            flexShrink: 0,
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {imgUrl ? (
              <img 
                src={imgUrl} 
                alt={titre} 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain',
                  background: '#fff'
                }} 
                onError={e => { e.target.onerror = null; e.target.src = '/no-image.png'; }} 
              />
            ) : (
              <ShoppingBag size={24} color="#bbb" />
            )}
          </Box>
          {/* Infos de base */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600,
                color: '#1a2233',
                fontSize: '0.875rem',
                mb: 0.5,
                maxWidth: 200,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {titre}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {flagUrlLivraison && (
                <img 
                  src={flagUrlLivraison} 
                  alt={row.PaysLivraison || row.Pays} 
                  style={{ 
                    width: 16, 
                    height: 12, 
                    borderRadius: 1,
                    border: '1px solid #eee'
                  }} 
                />
              )}
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                {row.PaysLivraison || row.Pays}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 600, fontSize: '0.95rem', ml: 1 }}>
                {formatDate(row.DateCommande)}
              </Typography>
            </Box>
            {/* Afficher la date de remboursement juste en dessous si Refund, en plus gros */}
            {row.Statut === 'Refund' && (
              <Typography variant="subtitle2" sx={{ color: '#ff9800', fontWeight: 700, display: 'block', mb: 0.5, fontSize: '0.95rem' }}>
                Remboursé le {formatDate(row.RefundDate)}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                {row.AmazonOrderId}
              </Typography>
              {orderLink && (
                <a 
                  href={orderLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={{ 
                    color: '#1976d2',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}
                >
                  <LinkIcon size={14} />
                </a>
              )}
              {/* Icône argent pour lien paiement Seller Central, dynamique selon marketplace */}
              {sellerCentralUrls[row.Pays] && (
                <a
                  href={sellerCentralUrls[row.Pays].replace('/orders-v3/order/', '/payments/event/view?accountType=ALL&orderId=') + row.AmazonOrderId + '&resultsPerPage=10&pageNumber=1'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#388e3c',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: 2
                  }}
                  title="Voir le paiement de cette commande sur Seller Central"
                >
                  <Banknote size={16} />
                </a>
              )}
            </Box>
            {/* Affichage de la quantité */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                Quantité : {row.Quantite || 1}
              </Typography>
            </Box>
            {/* Outils produit (Amazon, SellerAmp, Keepa) */}
            {asin && (
              <Box sx={{ mt: 1 }}>
                <ProductTools asin={asin} countryCode={row.Pays || 'FR'} />
              </Box>
            )}
          </Box>
        </Box>
        {/* Colonne centrale : Métriques financières ou remboursement */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          borderRight: { md: '1px solid #eee' },
          pr: { md: 2 },
          mr: { md: 2 }
        }}>
          {/* Afficher prix de vente et coût total même pour Refund */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                Prix de vente
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: '#1976d2',
                  fontWeight: 700,
                  fontSize: '1rem'
                }}
              >
                {formatAmount(row.PrixDeVente)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                Coût total
              </Typography>
              <Tooltip title={coutTotalTooltip} arrow>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: '#c62828',
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    borderBottom: '1px dotted #aaa',
                    display: 'inline-block'
                  }}
                >
                  {formatAmount(row.cout_achat_total)}
                </Typography>
              </Tooltip>
            </Box>
            {/* Réafficher le bloc Frais + TVA sauf pour Refund */}
            {row.Statut !== 'Refund' && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                  Frais + TVA
                </Typography>
                <Tooltip title={fraisEtTvaTooltip} arrow>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#666',
                      fontWeight: 600,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      borderBottom: '1px dotted #aaa',
                      display: 'inline-block'
                    }}
                  >
                    {formatAmount(totalFees)} + {formatAmount(row.TVA)}
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Box>
          {row.Statut === 'Refund' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>Perte</Typography>
                  <Typography variant="subtitle2" sx={{ color: '#c62828', fontWeight: 700 }}>
                    {formatAmount(row.RefundLossHT)}
                  </Typography>
                </Box>
                {/* Afficher le remboursement Amazon uniquement si différent de 0 */}
                {Number(row.RefundAmazon) !== 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666' }}>Remboursement Amazon</Typography>
                    <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                      {formatAmount(row.RefundAmazon)}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>Quantité</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {row.QteRefund}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>Raison</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {row.RefundReason}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>Catégorie</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {row.RefundCategory}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>État</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {row.EtatRefund}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>Statut</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {row.StatutRefund}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              {/* Indicateurs de performance */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                    Profit
                  </Typography>
                  <Box sx={{ 
                    bgcolor: profitColors.bg,
                    color: profitColors.text,
                    p: 1,
                    borderRadius: 1,
                    textAlign: 'center',
                    fontWeight: 600
                  }}>
                    {formatAmount(row.Profit)}
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                    ROI
                  </Typography>
                  <Box sx={{ 
                    bgcolor: roiColors.bg,
                    color: roiColors.text,
                    p: 1,
                    borderRadius: 1,
                    textAlign: 'center',
                    fontWeight: 600
                  }}>
                    {formatPercentage(row.ROI)}
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                    Marge
                  </Typography>
                  <Box sx={{ 
                    bgcolor: margeColors.bg,
                    color: margeColors.text,
                    p: 1,
                    borderRadius: 1,
                    textAlign: 'center',
                    fontWeight: 600
                  }}>
                    {formatPercentage(row.Marge)}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
        {/* Colonne de droite : Statut et actions */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'flex-end' },
          gap: 1,
          flex: { md: '0 0 120px' }
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 0.5 }}>
            <Chip 
              label={statut} 
              color={STATUS_COLORS[statut] || 'default'} 
              size="small" 
              sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600, px: 1, alignSelf: { xs: 'flex-start', md: 'flex-end' } }} 
            />
            {row.Business === true && (
              <Chip 
                label="Business" 
                color="info" 
                size="small" 
                sx={{ fontWeight: 700, mt: 0.5, background: '#e3f2fd', color: '#1976d2', alignSelf: { xs: 'flex-start', md: 'flex-end' } }} 
              />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Voir détail du produit">
              <IconButton 
                size="small" 
                onClick={() => handleOpenProductDetail(produit?.id)} 
                disabled={!produit}
                sx={{ width: 28, height: 28 }}
              >
                <Package size={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Voir détail de l'achat">
              <IconButton 
                size="small" 
                onClick={() => handleOpenAchatDetail(achat?.id)} 
                disabled={!achat}
                sx={{ width: 28, height: 28 }}
              >
                <LinkIcon size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Card>
    );
  };

  // Calculer le nombre d'éléments par statut
  const statusCounts = useMemo(() => {
    const counts = {};
    for (const cmd of commandes) {
      counts[cmd.Statut] = (counts[cmd.Statut] || 0) + 1;
    }
    return counts;
  }, [commandes]);

  return (
    <div style={{ padding: "32px 20px 32px 20px" }}>
      {/* Pastilles de statuts filtrables */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {ORDER_STATUSES.map(status => (
          <Chip
            key={status}
            label={status === 'Business'
              ? `Business (${commandes.filter(cmd => cmd.Business === true).length})`
              : statusCounts[status] ? `${status} (${statusCounts[status]})` : status}
            color={STATUS_COLORS[status] || 'default'}
            variant={statusFilter === status ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter(statusFilter === status ? null : status)}
            sx={{ fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
          />
        ))}
        {statusFilter && (
          <Button size="small" onClick={() => setStatusFilter(null)} sx={{ ml: 2 }}>Réinitialiser</Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ flexGrow: 1 }}>
          Commandes
        </Typography>
        <IconButton onClick={() => setIsThresholdsOpen(true)} color="primary" sx={{ mr: 1 }}>
          <Settings size={22} />
        </IconButton>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
          sx={{ bgcolor: '#f5f7fa', borderRadius: 2 }}
        >
          <ToggleButton value="table" sx={{ fontWeight: 600 }}>Tableau</ToggleButton>
          <ToggleButton value="card-table" sx={{ fontWeight: 600 }}>Moderne</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {viewMode === 'card-table' && (
        <Box sx={{ mb: 3 }}>
          <TextField
            label="Rechercher par ID de commande"
            variant="outlined"
            value={orderIdFilter}
            onChange={(e) => setOrderIdFilter(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <Search size={20} style={{ marginRight: 8, color: '#666' }} />
              ),
              endAdornment: orderIdFilter && (
                <IconButton
                  size="small"
                  onClick={() => setOrderIdFilter("")}
                  edge="end"
                >
                  <X size={16} />
                </IconButton>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                borderRadius: 2,
              },
            }}
          />
        </Box>
      )}
      <Drawer
        anchor="right"
        open={isThresholdsOpen}
        onClose={() => setIsThresholdsOpen(false)}
        PaperProps={{ sx: { width: 400 } }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Configuration des seuils</Typography>
            <IconButton onClick={() => setIsThresholdsOpen(false)}>
              <Settings size={20} />
            </IconButton>
          </Box>
          {Object.entries(thresholds).map(([key, threshold]) => (
            <Box key={key} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                  {key === 'marge' ? 'Marge' : key.charAt(0).toUpperCase() + key.slice(1)}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={threshold.enabled}
                      onChange={(e) => updateThreshold(key, 'enabled', e.target.checked)}
                    />
                  }
                  label="Actif"
                />
              </Box>
              {threshold.enabled && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography gutterBottom>Seuil haut</Typography>
                    <Slider
                      value={threshold.high}
                      onChange={(_, value) => updateThreshold(key, 'high', value)}
                      min={0}
                      max={key === 'roi' ? 200 : key === 'marge' ? 100 : 1000}
                      step={1}
                      marks
                      valueLabelDisplay="auto"
                    />
                    <TextField
                      size="small"
                      value={threshold.high}
                      onChange={(e) => updateThreshold(key, 'high', Number(e.target.value))}
                      type="number"
                      sx={{ mt: 1, width: '100%' }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography gutterBottom>Seuil moyen</Typography>
                    <Slider
                      value={threshold.medium}
                      onChange={(_, value) => updateThreshold(key, 'medium', value)}
                      min={0}
                      max={threshold.high}
                      step={1}
                      marks
                      valueLabelDisplay="auto"
                    />
                    <TextField
                      size="small"
                      value={threshold.medium}
                      onChange={(e) => updateThreshold(key, 'medium', Number(e.target.value))}
                      type="number"
                      sx={{ mt: 1, width: '100%' }}
                    />
                  </Box>
                </>
              )}
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))}
        </Box>
      </Drawer>
      {viewMode === 'table' ? (
        <>
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Recherche globale"
              variant="outlined"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
            />
          </Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6">Total des ventes</Typography>
                  <Typography variant="h4">{formatAmount(totals.totalVentes)}</Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6">Total des profits</Typography>
                  <Typography variant="h4">{formatAmount(totals.totalProfit)}</Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6">TVA collectée</Typography>
                  <Typography variant="h4">{formatAmount(totals.totalTVA)}</Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <TableContainer component={Paper}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            style={{
                              padding: "6px 8px",
                              borderBottom: "1px solid #ccc",
                              borderRight: "1px solid #eee",
                              textAlign: "left",
                              cursor: header.column.getCanSort() ? "pointer" : "default",
                              position: "relative",
                              width: header.getSize(),
                              minWidth: 50,
                              userSelect: "none",
                              fontSize: "0.875rem",
                            }}
                          >
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                cursor: 'move',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                }
                              }}
                              draggable
                              onDragStart={(e) => {
                                setDraggedColumnId(header.column.id);
                                e.currentTarget.style.opacity = '0.5';
                              }}
                              onDragEnd={(e) => {
                                setDraggedColumnId(null);
                                e.currentTarget.style.opacity = '1';
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                if (draggedColumnId && draggedColumnId !== header.column.id) {
                                  const newOrder = table.getAllLeafColumns()
                                    .map(col => col.id)
                                    .filter(id => id !== draggedColumnId);
                                  const targetIndex = newOrder.indexOf(header.column.id);
                                  newOrder.splice(targetIndex, 0, draggedColumnId);
                                  table.setColumnOrder(newOrder);
                                }
                              }}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <GripVertical style={{ marginRight: 4, opacity: 0.2, fontSize: '1rem', '&:hover': { opacity: 0.5 } }} />
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <ArrowUp size={16} style={{ marginLeft: 4 }} />, 
                                desc: <ArrowDown size={16} style={{ marginLeft: 4 }} />,
                              }[header.column.getIsSorted()] ?? null}
                            </Box>
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                height: '100%',
                                width: '4px',
                                cursor: 'col-resize',
                                userSelect: 'none',
                                touchAction: 'none',
                                backgroundColor: '#e0e0e0',
                                opacity: 0.5,
                                transition: 'opacity 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.backgroundColor = '#2196f3';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '0.5';
                                e.currentTarget.style.backgroundColor = '#e0e0e0';
                              }}
                            />
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    <tr>
                      {table.getHeaderGroups()[0].headers.map((header) => {
                        const columnId = header.column.id;
                        const filter = filters[columnId];
                        if (columnId === 'Statut') {
                          return (
                            <td key={`filter-${columnId}`} style={{ padding: "4px 8px", borderBottom: "1px solid #eee", borderRight: "1px solid #eee", backgroundColor: "#f5f5f5", fontSize: "0.875rem", minHeight: "40px" }}>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={filters.Statut.value}
                                  onChange={e => {
                                    const newFilter = { value: e.target.value };
                                    setFilters(prev => ({ ...prev, Statut: newFilter }));
                                    header.column.setFilterValue(newFilter);
                                  }}
                                  displayEmpty
                                  size="small"
                                >
                                  <MenuItem value="">Tous</MenuItem>
                                  {ORDER_STATUSES.map(status => (
                                    <MenuItem key={status} value={status}>{status}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </td>
                          );
                        }
                        if (filter) {
                          // Filtres texte ou liste (ex: Produit)
                          if (columnId === 'Produit') {
                            let uniqueValues = produits.map(p => p.Titre).filter(Boolean).sort();
                            return (
                              <td key={`filter-${columnId}`} style={{ padding: "4px 8px", borderBottom: "1px solid #eee", borderRight: "1px solid #eee", backgroundColor: "#f5f5f5", fontSize: "0.875rem", minHeight: "40px" }}>
                                <Autocomplete
                                  size="small"
                                  options={uniqueValues}
                                  value={filter.value || ''}
                                  onChange={(_, newValue) => {
                                    const newFilter = { ...filter, value: newValue };
                                    setFilters(prev => ({ ...prev, [columnId]: newFilter }));
                                    header.column.setFilterValue(newFilter);
                                  }}
                                  renderInput={(params) => (
                                    <TextField {...params} placeholder="Rechercher..." size="small" fullWidth />
                                  )}
                                  freeSolo
                                  disableClearable
                                  sx={{ width: '100%' }}
                                />
                                {filter.value && (
                                  <IconButton size="small" onClick={() => {
                                    const newFilter = { ...filter, value: '' };
                                    setFilters(prev => ({ ...prev, [columnId]: newFilter }));
                                    header.column.setFilterValue(newFilter);
                                  }} sx={{ ml: 0.5 }}>
                                    <X size={16} />
                                  </IconButton>
                                )}
                              </td>
                            );
                          }
                          // Filtres numériques
                          return (
                            <td key={`filter-${columnId}`} style={{ padding: "4px 8px", borderBottom: "1px solid #eee", borderRight: "1px solid #eee", backgroundColor: "#f5f5f5", fontSize: "0.875rem", minHeight: "40px" }}>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%' }}>
                                <FormControl size="small" sx={{ minWidth: 60 }}>
                                  <Select
                                    value={filter.operator || '>='}
                                    onChange={(e) => {
                                      const newFilter = { ...filter, operator: e.target.value };
                                      setFilters(prev => ({ ...prev, [columnId]: newFilter }));
                                      header.column.setFilterValue(newFilter);
                                    }}
                                    size="small"
                                    sx={{ height: '36px' }}
                                  >
                                    <MenuItem value=">=">≥</MenuItem>
                                    <MenuItem value=">">&gt;</MenuItem>
                                    <MenuItem value="<=">≤</MenuItem>
                                    <MenuItem value="<">&lt;</MenuItem>
                                    <MenuItem value="=">=</MenuItem>
                                  </Select>
                                </FormControl>
                                <TextField
                                  size="small"
                                  value={filter.value}
                                  onChange={(e) => {
                                    const newFilter = { ...filter, value: e.target.value };
                                    setFilters(prev => ({ ...prev, [columnId]: newFilter }));
                                    header.column.setFilterValue(newFilter);
                                  }}
                                  type="number"
                                  sx={{ width: 60 }}
                                />
                                {filter.value && (
                                  <IconButton size="small" onClick={() => {
                                    const newFilter = { ...filter, value: '' };
                                    setFilters(prev => ({ ...prev, [columnId]: newFilter }));
                                    header.column.setFilterValue(newFilter);
                                  }}>
                                    <X size={16} />
                                  </IconButton>
                                )}
                              </Box>
                            </td>
                          );
                        }
                        return (
                          <td key={`filter-${columnId}`} style={{ padding: "4px 8px", borderBottom: "1px solid #eee", borderRight: "1px solid #eee", backgroundColor: "#f5f5f5", fontSize: "0.875rem", minHeight: "40px" }} />
                        );
                      })}
                    </tr>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            style={{
                              padding: "6px 8px",
                              borderBottom: "1px solid #eee",
                              borderRight: "1px solid #eee",
                              width: cell.column.getSize(),
                              minWidth: 50,
                              maxWidth: cell.column.getSize(),
                              fontSize: "0.875rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              position: "relative"
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableContainer>
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => setPagination((prev) => ({ ...prev, pageIndex: Math.max(prev.pageIndex - 1, 0) }))}
                  disabled={!table.getCanPreviousPage()}
                >
                  Précédent
                </Button>
                <span>
                  Page {pagination.pageIndex + 1} sur {pageCount}
                </span>
                <Button
                  variant="contained"
                  onClick={() => setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }))}
                  disabled={!table.getCanNextPage()}
                >
                  Suivant
                </Button>
              </Box>
            </>
          )}
        </>
      ) : (
        <Box sx={{ mt: 2 }}>
          {paginatedCommandes.map(row => (
            <Box key={row.id} sx={{ py: 1, px: { xs: 0, md: 1 }, background: 'transparent' }}>
              {renderCard(row)}
            </Box>
          ))}
          {displayCount < filteredCommandes.length && (
            <Button
              variant="outlined"
              onClick={() => setDisplayCount(prev => prev + 10)}
              sx={{ mt: 2 }}
            >
              Charger plus
            </Button>
          )}
        </Box>
      )}
      <Dialog open={openProductDialog} onClose={handleCloseProductDialog} fullWidth maxWidth="xl">
        <DialogTitle>Détails du Produit</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {selectedProduct ? (
            <Produit
              produit={selectedProduct}
              thresholds={thresholds}
              setSelectedProduct={setSelectedProduct}
              setOpenRepricerModal={setOpenRepricerModal}
              setAsinToRefresh={() => {}}
              setOpenConfirmRefresh={() => {}}
              purgeRestrictionsCacheForAsin={() => {}}
              setRefreshRestrictionsTrigger={() => {}}
              fournisseurs={fournisseurs || []}
              onEdit={handleOpenProductDetail}
              onDelete={() => {}}
            />
          ) : (
            <Typography>Aucun produit sélectionné</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProductDialog} variant="contained" color="primary">Fermer</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openAchatDialog} onClose={handleCloseAchatDialog} fullWidth maxWidth="sm">
        <DialogTitle>Détails de l'Achat</DialogTitle>
        <DialogContent dividers>
          {selectedAchat ? (
            <div style={{ padding: "16px" }}>
              <Typography variant="h6" gutterBottom>
                Achat #{selectedAchat.id}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Date d'achat :</strong> {formatDate(selectedAchat.dateAchat)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Quantité :</strong> {selectedAchat.Quantité}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Prix d'achat :</strong> {formatAmount(selectedAchat.prixAchat)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>SKUID :</strong> {selectedAchat.SKUID}
              </Typography>
            </div>
          ) : (
            <Typography>Aucun achat sélectionné</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAchatDialog} variant="contained" color="primary">Fermer</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CommandesPage; 